import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Rocket, ArrowRight } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  // Phase 19.21 — refresh the cached SubscriptionContext after sync so
  // SubscriptionGate on /dashboard sees `active|trialing` instead of the
  // stale `incomplete` it had cached at app boot.
  const { refreshSubscription } = useSubscription();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    let cancelled = false;

    // Phase 19.21 — force-sync from Stripe instead of waiting for webhook,
    // then poll /status briefly until we see active|trialing. This prevents
    // the post-signup bounce to /manage-subscription.
    const syncAndPoll = async () => {
      try {
        await fetch(`${API_URL}/api/subscriptions/sync`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } catch (e) {
        console.warn('subscription sync failed (will fall back to /status polling):', e);
      }

      const isReady = (s) =>
        s && s.has_subscription && ['active', 'trialing'].includes(s.status);

      // Poll up to ~12s (8 tries × 1.5s) for the status to flip.
      let last = null;
      for (let i = 0; i < 8 && !cancelled; i++) {
        try {
          const r = await fetch(`${API_URL}/api/subscriptions/status`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (r.ok) {
            last = await r.json();
            if (isReady(last)) break;
          }
        } catch (e) {
          // continue polling
        }
        await new Promise((res) => setTimeout(res, 1500));
      }
      if (!cancelled) {
        setSubscription(last);
        // CRITICAL: refresh the cached context BEFORE enabling the
        // "Go to Dashboard" CTA, so SubscriptionGate on /dashboard reads
        // the fresh active status — not the stale incomplete one cached
        // at app boot.
        try {
          await refreshSubscription();
        } catch (e) {
          console.warn('subscription context refresh failed:', e);
        }
        setLoading(false);
      }
    };

    syncAndPoll();
    return () => { cancelled = true; };
  }, [navigate, refreshSubscription]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-cyan-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Setting up your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        <CardContent className="pt-8 pb-6 text-center space-y-6">
          {/* Success Icon */}
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
            <CheckCircle className="h-10 w-10 text-green-400" />
          </div>

          {/* Title */}
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Welcome to Business Genie!
            </h1>
            <p className="text-slate-400">
              Your {subscription?.plan_name || 'subscription'} is now active.
            </p>
          </div>

          {/* Trial Info */}
          {subscription?.status === 'trialing' && subscription?.trial_ends_at && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
              <p className="text-cyan-400 text-sm">
                Your 30-day free trial has started. You won't be charged until{' '}
                {new Date(subscription.trial_ends_at).toLocaleDateString()}.
              </p>
            </div>
          )}

          {/* What's Included */}
          <div className="bg-slate-900/50 rounded-lg p-4 text-left">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Rocket className="h-4 w-4 text-cyan-400" />
              What's included:
            </h3>
            <ul className="space-y-2 text-sm text-slate-300">
              {subscription?.features?.slice(0, 5).map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <CheckCircle className="h-3.5 w-3.5 text-green-400" />
                  {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </li>
              ))}
              {(subscription?.features?.length || 0) > 5 && (
                <li className="text-slate-500">
                  +{subscription.features.length - 5} more features
                </li>
              )}
            </ul>
          </div>

          {/* CTA */}
          <Button
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>

          <p className="text-xs text-slate-500">
            Need help? Contact us at support@businessgenie.com
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
